import { useState, useMemo } from 'react';
import { ZoomIn, ZoomOut, Maximize2, ArrowUpDown } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { getTimingBreakdown, getFileName, formatTime } from '@har-insight/parser';
import { Dropdown, DropdownItem } from '@/components/Dropdown';
import { cn } from '@/lib/utils';

const TIMING_COLORS = {
  Blocked: 'bg-slate-500 dark:bg-slate-400',
  DNS: 'bg-lime-500 dark:bg-lime-400',
  Connect: 'bg-orange-500 dark:bg-orange-400',
  SSL: 'bg-violet-500 dark:bg-violet-400',
  Send: 'bg-blue-500 dark:bg-blue-400',
  Wait: 'bg-emerald-500 dark:bg-emerald-400',
  Receive: 'bg-cyan-500 dark:bg-cyan-400',
};

const TIMING_DESCRIPTIONS: Record<string, string> = {
  Blocked: 'Time spent in queue waiting for a network connection',
  DNS: 'DNS resolution time - converting domain name to IP address',
  Connect: 'TCP connection establishment time',
  SSL: 'SSL/TLS handshake time for secure HTTPS connections',
  Send: 'Time spent sending the HTTP request to the server',
  Wait: 'Time waiting for server response (TTFB - Time To First Byte)',
  Receive: 'Time spent downloading the response data from server',
};

const MIN_BAR_WIDTH_PX = 3;
const NO_TIMING_COLOR = 'bg-slate-400 dark:bg-slate-500';

type ViewMode = 'waterfall' | 'gantt';
type SortMode = 'startTime' | 'duration' | 'size' | 'name';

const SORT_OPTIONS: { key: SortMode; label: string }[] = [
  { key: 'startTime', label: 'Start Time' },
  { key: 'duration', label: 'Duration' },
  { key: 'size', label: 'Size' },
  { key: 'name', label: 'Name' },
];

const ZOOM_LEVELS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 5];
const GRID_GUIDES = [0.25, 0.5, 0.75];
const NAME_COL_WIDTH = 180;
const TIME_COL_WIDTH = 72;
const WATERFALL_MIN_WIDTH = 720;
const WATERFALL_BASE_WIDTH = 620;

type ZoomPreset = 'manual' | 'fitAll' | 'fitSelected';

function getClosestZoomIndex(desiredZoom: number) {
  const clampedZoom = Math.max(ZOOM_LEVELS[0], Math.min(desiredZoom, ZOOM_LEVELS[ZOOM_LEVELS.length - 1]));
  return ZOOM_LEVELS.reduce((bestIndex, current, currentIndex) => {
    const currentDelta = Math.abs(current - clampedZoom);
    const bestDelta = Math.abs(ZOOM_LEVELS[bestIndex] - clampedZoom);
    return currentDelta < bestDelta ? currentIndex : bestIndex;
  }, 0);
}

export function WaterfallChart() {
  const { har, filteredEntries, selectedEntry, selectEntry, stats } = useHARStore();
  const [viewMode, setViewMode] = useState<ViewMode>('waterfall');
  const [sortMode, setSortMode] = useState<SortMode>('startTime');
  const [zoomIndex, setZoomIndex] = useState(3); // Default to 1x
  const [zoomPreset, setZoomPreset] = useState<ZoomPreset>('fitAll');

  const { maxTime, maxDuration, p95Time, entries, pageEvents, maxEventTime } = useMemo(() => {
    if (!stats || filteredEntries.length === 0) {
      return { maxTime: 0, maxDuration: 0, p95Time: 0, entries: [], pageEvents: [], maxEventTime: 0 };
    }

    // Sort based on selected sort mode
    const sorted = [...filteredEntries].sort((a, b) => {
      switch (sortMode) {
        case 'startTime':
          return (a._startTime || 0) - (b._startTime || 0);
        case 'duration':
          return (b.time || 0) - (a.time || 0); // Longest first
        case 'size':
          return (b.response.content.size || 0) - (a.response.content.size || 0); // Largest first
        case 'name':
          return getFileName(a.request.url).localeCompare(getFileName(b.request.url));
        default:
          return 0;
      }
    });

    const endTimes = sorted.map(e => e._endTime || 0);
    const durations = sorted.map(e => e.time || 0).sort((a, b) => a - b);

    const maxTime = Math.max(...endTimes);
    const maxDuration = Math.max(...durations);

    // Calculate 95th percentile for smart zoom
    const p95Index = Math.floor(durations.length * 0.95);
    const p95Time = durations[p95Index] || maxDuration;

    const entries = sorted.map(entry => ({
      entry,
      startTime: entry._startTime || 0,
      timings: getTimingBreakdown(entry.timings),
      totalTime: entry.time || 0,
    }));

    // Get page events (DOMContentLoaded, Load)
    const pageEvents: { name: string; time: number; color: string }[] = [];
    if (har?.log.pages?.[0]?.pageTimings) {
      const pt = har.log.pages[0].pageTimings;
      if (pt.onContentLoad && pt.onContentLoad > 0) {
        pageEvents.push({ name: 'DCL', time: pt.onContentLoad, color: '#3b82f6' });
      }
      if (pt.onLoad && pt.onLoad > 0) {
        pageEvents.push({ name: 'Load', time: pt.onLoad, color: '#ef4444' });
      }
    }

    const maxEventTime = Math.max(0, ...pageEvents.map(e => e.time));

    return { maxTime, maxDuration, p95Time, entries, pageEvents, maxEventTime };
  }, [har, filteredEntries, stats, sortMode]);

  // Auto-fit: use p95 as base scale to avoid outliers dominating
  const baseScale = useMemo(() => {
    if (viewMode === 'waterfall') {
      // For waterfall, check if there are outliers
      const hasOutliers = maxTime > p95Time * 2;
      return hasOutliers ? maxTime * 0.6 : maxTime;
    } else {
      const hasOutliers = maxDuration > p95Time * 2;
      return hasOutliers ? p95Time * 1.5 : maxDuration;
    }
  }, [viewMode, maxTime, maxDuration, p95Time]);

  const selectedMetrics = useMemo(() => {
    if (!selectedEntry) return null;

    const selected = entries.find(item => item.entry._id === selectedEntry._id);
    if (!selected) return null;

    const selectedEnd = selected.entry._endTime || selected.startTime + selected.totalTime;
    return {
      totalTime: selected.totalTime,
      endTime: selectedEnd,
    };
  }, [entries, selectedEntry]);

  const activeZoomIndex = useMemo(() => {
    if (zoomPreset === 'manual') return zoomIndex;

    const fitAllSpanRaw = isFinite(maxTime) ? Math.max(maxTime, maxEventTime, 1) : 1;
    const fitAllSpan = viewMode === 'waterfall' ? fitAllSpanRaw : Math.max(maxDuration, 1);
    const fitAllDesired = baseScale / (fitAllSpan * 1.05);

    if (zoomPreset === 'fitSelected' && selectedMetrics) {
      const selectedSpanRaw = viewMode === 'waterfall' ? selectedMetrics.endTime : selectedMetrics.totalTime;
      const selectedSpan = Math.max(selectedSpanRaw, 1);
      return getClosestZoomIndex(baseScale / (selectedSpan * 1.2));
    }

    return getClosestZoomIndex(fitAllDesired);
  }, [zoomPreset, zoomIndex, viewMode, maxTime, maxEventTime, maxDuration, baseScale, selectedMetrics]);

  const zoom = ZOOM_LEVELS[activeZoomIndex];

  // Safety check: ensure scaleMax is always > 0 to avoid division by zero
  const scaleMax = Math.max(baseScale / zoom, 1);

  const handleZoomIn = () => {
    setZoomPreset('manual');
    setZoomIndex(i => Math.min(i + 1, ZOOM_LEVELS.length - 1));
  };
  const handleZoomOut = () => {
    setZoomPreset('manual');
    setZoomIndex(i => Math.max(i - 1, 0));
  };
  const handleReset = () => {
    setZoomPreset('manual');
    setZoomIndex(3);
  };

  if (entries.length === 0) {
    return (
      <div className="flex items-center justify-center h-[400px] text-slate-400 dark:text-slate-500">
        No requests to display
      </div>
    );
  }

  const isWaterfall = viewMode === 'waterfall';

  return (
    <div className="space-y-3">
      {/* Header with controls */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(TIMING_COLORS).map(([name, color]) => (
            <div key={name} className="flex items-center gap-1" title={`${name}: ${TIMING_DESCRIPTIONS[name]}`}>
              <div className={cn('w-2 h-2 rounded-sm', color)} />
              <span className="text-slate-400 dark:text-slate-500">{name}</span>
            </div>
          ))}
          {pageEvents.length > 0 && (
            <>
              <span className="text-slate-300 dark:text-slate-600">|</span>
              {pageEvents.map((event, i) => (
                <div key={i} className="flex items-center gap-1">
                  <div className="w-0.5 h-3" style={{ backgroundColor: event.color }} />
                  <span className="text-slate-400 dark:text-slate-500">{event.name}</span>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Sort dropdown */}
          <Dropdown
            trigger={
              <button
                className="p-1 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded"
                title={`Sort by: ${SORT_OPTIONS.find(o => o.key === sortMode)?.label}`}
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
              </button>
            }
          >
            {SORT_OPTIONS.map(option => (
              <DropdownItem
                key={option.key}
                onClick={() => setSortMode(option.key)}
                active={sortMode === option.key}
                className="text-xs py-1.5"
              >
                {option.label}
              </DropdownItem>
            ))}
          </Dropdown>

          {/* Zoom controls */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded p-0.5">
            <button
              onClick={handleZoomOut}
              disabled={activeZoomIndex === 0}
              className="p-1 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 disabled:opacity-30"
              title="Zoom out"
            >
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <span className="text-xs text-slate-500 w-8 text-center">{zoom}x</span>
            <button
              onClick={handleZoomIn}
              disabled={activeZoomIndex === ZOOM_LEVELS.length - 1}
              className="p-1 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 disabled:opacity-30"
              title="Zoom in"
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={handleReset}
              className="p-1 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              title="Reset zoom"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Zoom presets */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded p-0.5">
            <button
              onClick={() => setZoomPreset('fitAll')}
              className={cn(
                'px-1.5 py-1 text-[11px] rounded transition-colors',
                zoomPreset === 'fitAll'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              )}
              title="Fit all requests to the viewport"
            >
              Fit
            </button>
            <button
              onClick={() => selectedMetrics && setZoomPreset('fitSelected')}
              disabled={!selectedMetrics}
              className={cn(
                'px-1.5 py-1 text-[11px] rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
                zoomPreset === 'fitSelected'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              )}
              title={selectedMetrics ? 'Fit to selected request' : 'Select a request to focus'}
            >
              Selected
            </button>
          </div>

          {/* View toggle */}
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded p-0.5">
            <button
              onClick={() => setViewMode('waterfall')}
              className={cn(
                'px-2 py-0.5 text-xs font-medium rounded transition-colors',
                viewMode === 'waterfall'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              )}
            >
              Waterfall
            </button>
            <button
              onClick={() => setViewMode('gantt')}
              className={cn(
                'px-2 py-0.5 text-xs font-medium rounded transition-colors',
                viewMode === 'gantt'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              )}
            >
              Gantt
            </button>
          </div>
        </div>
      </div>

      {/* Chart with horizontal scroll */}
      <div className="relative">
        {/* Vertical scroll container */}
        <div className="max-h-[500px] overflow-y-auto">
          {/* Horizontal scroll container */}
          <div className="overflow-x-auto">
            <div style={{ minWidth: `${Math.max(WATERFALL_MIN_WIDTH, WATERFALL_BASE_WIDTH * zoom)}px` }}>
              {/* Time scale header */}
              <div className="flex items-center gap-2 py-1 px-2 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
                <div style={{ width: `${NAME_COL_WIDTH}px` }} className="flex-shrink-0 sticky left-0 z-20 bg-white dark:bg-slate-900" />
                <div className="flex-1 flex justify-between text-[10px] text-slate-400 dark:text-slate-600">
                  <span>0</span>
                  <span>{formatTime(scaleMax / 4)}</span>
                  <span>{formatTime(scaleMax / 2)}</span>
                  <span>{formatTime((scaleMax * 3) / 4)}</span>
                  <span>{formatTime(scaleMax)}</span>
                </div>
                <div style={{ width: `${TIME_COL_WIDTH}px` }} className="flex-shrink-0 sticky right-0 z-20 bg-white dark:bg-slate-900" />
              </div>

              {entries.map(({ entry, startTime, timings, totalTime }, index) => {
                const isSelected = selectedEntry?._id === entry._id;
                const fileName = getFileName(entry.request.url);
                const endTime = entry._endTime || startTime + totalTime;
                const isOverflow = isWaterfall && endTime > scaleMax;
                const hasNoTimingData = !timings || timings.length === 0 || totalTime === 0;

                return (
                  <div
                    key={entry._id || index}
                    onClick={() => selectEntry(entry)}
                    className={cn(
                      'group flex items-center gap-2 py-0.5 px-2 rounded cursor-pointer transition-colors',
                      'hover:bg-slate-100 dark:hover:bg-slate-800/50',
                      index % 2 === 0 && 'bg-slate-50/30 dark:bg-slate-900/20',
                      isSelected && 'bg-brand-500/10'
                    )}
                  >
                    {/* File name */}
                    <div
                      style={{ width: `${NAME_COL_WIDTH}px` }}
                      className={cn(
                        'flex-shrink-0 sticky left-0 z-20 pr-2 rounded-l-md backdrop-blur-sm',
                        isSelected
                          ? 'bg-brand-500/10'
                          : 'bg-white/95 dark:bg-slate-900/95 group-hover:bg-slate-100 dark:group-hover:bg-slate-800/60'
                      )}
                    >
                      <span
                        className="text-xs text-slate-500 dark:text-slate-400 truncate block"
                        title={entry.request.url}
                      >
                        {fileName}
                      </span>
                    </div>

                    {/* Timing bar */}
                    <div className="flex-1 h-3.5 bg-slate-200/60 dark:bg-slate-800/60 rounded relative overflow-hidden border border-slate-200/80 dark:border-slate-700/60">
                      {/* Readability guides */}
                      {GRID_GUIDES.map(guide => (
                        <div
                          key={guide}
                          className="absolute top-0 bottom-0 w-px bg-slate-300/60 dark:bg-slate-700/80"
                          style={{ left: `${guide * 100}%` }}
                        />
                      ))}

                      {/* Page event markers */}
                      {isWaterfall && pageEvents.map((event, i) => {
                        const pos = (event.time / scaleMax) * 100;
                        if (pos > 100) return null;
                        return (
                          <div
                            key={i}
                            className="absolute top-0 bottom-0 w-px z-10"
                            style={{ left: `${pos}%`, backgroundColor: event.color }}
                            title={`${event.name}: ${formatTime(event.time)}`}
                          />
                        );
                      })}
                      {/* Request bar container - positioned at start time with width = duration */}
                      {(() => {
                        const barLeftPercent = isWaterfall ? (startTime / scaleMax) * 100 : 0;
                        const barWidthPercent = (totalTime / scaleMax) * 100;

                        // Don't render if completely off-screen
                        if (barLeftPercent > 100) return null;

                        // For zero/very small durations, show a minimal indicator
                        const isMinimalDuration = totalTime === 0 || totalTime < 1;
                        const actualBarWidthPercent = isMinimalDuration
                          ? (MIN_BAR_WIDTH_PX / 400) * 100 // Approximate percentage for 3px at default scale
                          : Math.max(barWidthPercent, (MIN_BAR_WIDTH_PX / 400) * 100);

                        return (
                          <div
                            className="absolute h-full flex items-center"
                            style={{
                              left: `${Math.min(barLeftPercent, 100)}%`,
                              width: `${Math.max(Math.min(actualBarWidthPercent, 100 - barLeftPercent), 0.5)}%`,
                              minWidth: `${MIN_BAR_WIDTH_PX}px`,
                            }}
                          >
                            {hasNoTimingData ? (
                              // Fallback: single colored bar when no timing data available
                              <div
                                className={cn('h-2.5 w-full rounded-sm', NO_TIMING_COLOR)}
                                title={totalTime === 0 ? 'No timing data available' : `Duration: ${formatTime(totalTime)} (no breakdown)`}
                              >
                                {isMinimalDuration && (
                                  <div className="w-1.5 h-1.5 rounded-full bg-slate-600 dark:bg-slate-300 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                                )}
                              </div>
                            ) : (
                              // Timing segments - sized relative to request's total time
                              timings.map((timing, i) => {
                                const segmentWidthPercent = totalTime > 0 ? (timing.duration / totalTime) * 100 : 0;
                                if (segmentWidthPercent < 0.5) return null;

                                const colorClass = TIMING_COLORS[timing.name as keyof typeof TIMING_COLORS] || 'bg-slate-600';

                                return (
                                  <div
                                    key={i}
                                    className={cn('h-full flex-shrink-0 ring-1 ring-black/10 dark:ring-white/10', colorClass)}
                                    style={{
                                      width: `${segmentWidthPercent}%`,
                                      minWidth: '1px',
                                    }}
                                    title={`${timing.name}: ${formatTime(timing.duration)} - ${TIMING_DESCRIPTIONS[timing.name]}`}
                                  />
                                );
                              })
                            )}
                          </div>
                        );
                      })()}
                      {/* Overflow indicator */}
                      {isOverflow && (
                        <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-slate-300 dark:from-slate-600 to-transparent z-20" />
                      )}
                    </div>

                    {/* Time */}
                    <div
                      style={{ width: `${TIME_COL_WIDTH}px` }}
                      className={cn(
                        'flex-shrink-0 text-right sticky right-0 z-20 pl-2 rounded-r-md backdrop-blur-sm',
                        isSelected
                          ? 'bg-brand-500/10'
                          : 'bg-white/95 dark:bg-slate-900/95 group-hover:bg-slate-100 dark:group-hover:bg-slate-800/60'
                      )}
                    >
                      <span className={cn(
                        'text-xs font-mono',
                        totalTime > 1000 ? 'text-yellow-600 dark:text-yellow-400' : 'text-slate-400 dark:text-slate-500',
                        totalTime > 3000 && 'text-red-600 dark:text-red-400'
                      )}>
                        {formatTime(totalTime)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Horizontal scrollbar indicator */}
        {zoom > 1 && (
          <div className="text-center text-[10px] text-slate-400 dark:text-slate-600 mt-1">
            ← Scroll horizontally to see more →
          </div>
        )}
      </div>
    </div>
  );
}
